import React, { useState } from 'react';
import RialSymbol from '../components/RialSymbol';
import { useNavigate, useLocation } from 'react-router-dom';
import customerService from '../services/customer.service';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Divider,
  Alert,
  Stepper,
  Step,
  StepLabel,
  TextField,
} from '@mui/material';
import {
  CreditCard as CardIcon,
  AccountBalance as BankIcon,
  Money as CashIcon,
  CheckCircle as SuccessIcon,
} from '@mui/icons-material';

interface CheckoutState {
  cart: any;
  total: number;
  discount: number;
  couponCode: string;
}

const steps = ['Review Order', 'Payment', 'Confirmation'];

export default function Checkout() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as CheckoutState;

  const [activeStep, setActiveStep] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [orderId, setOrderId] = useState('');

  // Card details state
  const [cardDetails, setCardDetails] = useState({
    number: '',
    name: '',
    expiry: '',
    cvv: '',
  });

  if (!state || !state.cart) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          No items in checkout
        </Typography>
        <Button variant="contained" onClick={() => navigate('/cart')}>
          Go to Cart
        </Button>
      </Box>
    );
  }

  const { cart, total, discount, couponCode } = state;

  const handleNext = () => {
    if (activeStep === 0) {
      // Proceed to payment
      setActiveStep(1);
    } else if (activeStep === 1) {
      // Validate payment details before processing
      if (paymentMethod === 'card') {
        // Validate card details
        if (!cardDetails.number || !cardDetails.name || !cardDetails.expiry || !cardDetails.cvv) {
          setError('Please fill in all card details');
          return;
        }

        // Basic validation
        if (cardDetails.number.replace(/\s/g, '').length < 13) {
          setError('Please enter a valid card number');
          return;
        }

        if (cardDetails.expiry.length < 5 || !cardDetails.expiry.includes('/')) {
          setError('Please enter a valid expiry date (MM/YY)');
          return;
        }

        if (cardDetails.cvv.length < 3) {
          setError('Please enter a valid CVV');
          return;
        }
      }

      // Process payment
      handlePayment();
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handlePayment = async () => {
    setLoading(true);
    setError('');

    try {
      // Simulate payment processing
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Create order
      const orderData = {
        items: cart.items.map((item: any) => ({
          surpriseBox: item.surpriseBox._id || item.surpriseBox,
          quantity: item.quantity,
          price: item.price,
        })),
        paymentMethod,
        couponCode: couponCode || undefined,
        discount: discount || 0,
        totalAmount: total,
      };

      // Call backend API to create order
      const createdOrder = await customerService.createOrder(orderData);
      console.log('Order created:', createdOrder);
      setOrderId(createdOrder._id);

      // Clear cart
      await customerService.clearCart();

      // Move to confirmation
      setActiveStep(2);
    } catch (err: any) {
      console.error('Payment error:', err);
      const errorMessage = err.response?.data?.error?.message || err.message || 'Payment failed. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrder = () => {
    if (orderId) {
      navigate(`/orders/${orderId}`);
    } else {
      navigate('/orders');
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
        Checkout
      </Typography>

      {/* Stepper */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* Step 0: Review Order */}
      {activeStep === 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Order Summary
              </Typography>

              {cart.items.map((item: any) => (
                <Box
                  key={item._id}
                  sx={{
                    display: 'flex',
                    gap: 2,
                    mb: 3,
                    pb: 3,
                    borderBottom: '1px solid #eee',
                  }}
                >
                  <Box
                    component="img"
                    src={item.surpriseBox?.images?.[0] || 'https://via.placeholder.com/80'}
                    alt={item.surpriseBox?.name}
                    sx={{
                      width: 80,
                      height: 80,
                      objectFit: 'cover',
                      borderRadius: 1,
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {item.surpriseBox?.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.surpriseBox?.business?.name}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Qty: {item.quantity} × <RialSymbol /> {item.price.toFixed(2)} = <RialSymbol />{' '}
                      {(item.quantity * item.price).toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Payment Details
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography color="text.secondary">Subtotal</Typography>
                <Typography sx={{ fontWeight: 600 }}>
                  <RialSymbol /> {cart.totalAmount.toFixed(2)}
                </Typography>
              </Box>

              {discount > 0 && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography color="success.main">
                      Discount ({discount}%)
                    </Typography>
                    <Typography sx={{ fontWeight: 600, color: 'success.main' }}>
                      - <RialSymbol /> {((cart.totalAmount * discount) / 100).toFixed(2)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Coupon: {couponCode}
                    </Typography>
                  </Box>
                </>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography color="text.secondary">Delivery Fee</Typography>
                <Typography sx={{ fontWeight: 600, color: 'success.main' }}>
                  FREE
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Total
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  <RialSymbol /> {total.toFixed(2)}
                </Typography>
              </Box>

              <Button fullWidth variant="contained" size="large" onClick={handleNext}>
                Proceed to Payment
              </Button>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Step 1: Payment */}
      {activeStep === 1 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Select Payment Method
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <FormControl component="fieldset" fullWidth>
                <RadioGroup value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                  <Card variant="outlined" sx={{ mb: 2, cursor: 'pointer' }}>
                    <CardContent>
                      <FormControlLabel
                        value="card"
                        control={<Radio />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <CardIcon />
                            <Box>
                              <Typography sx={{ fontWeight: 600 }}>
                                Credit/Debit Card
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Pay securely with your card
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </CardContent>
                  </Card>

                  <Card variant="outlined" sx={{ mb: 2, cursor: 'pointer' }}>
                    <CardContent>
                      <FormControlLabel
                        value="bank"
                        control={<Radio />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <BankIcon />
                            <Box>
                              <Typography sx={{ fontWeight: 600 }}>Bank Transfer</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Transfer directly from your bank
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </CardContent>
                  </Card>

                  <Card variant="outlined" sx={{ cursor: 'pointer' }}>
                    <CardContent>
                      <FormControlLabel
                        value="cash"
                        control={<Radio />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <CashIcon />
                            <Box>
                              <Typography sx={{ fontWeight: 600 }}>
                                Cash on Pickup
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Pay when you collect your order
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </CardContent>
                  </Card>
                </RadioGroup>
              </FormControl>

              {paymentMethod === 'card' && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                    Card Details <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    required
                    label="Card Number"
                    placeholder="1234 5678 9012 3456"
                    value={cardDetails.number}
                    onChange={(e) => {
                      // Format card number with spaces
                      const value = e.target.value.replace(/\s/g, '').replace(/(\d{4})/g, '$1 ').trim();
                      setCardDetails({ ...cardDetails, number: value });
                    }}
                    slotProps={{ htmlInput: { maxLength: 19 } }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    required
                    label="Cardholder Name"
                    placeholder="John Doe"
                    value={cardDetails.name}
                    onChange={(e) =>
                      setCardDetails({ ...cardDetails, name: e.target.value.toUpperCase() })
                    }
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      fullWidth
                      required
                      label="Expiry Date"
                      placeholder="MM/YY"
                      value={cardDetails.expiry}
                      onChange={(e) => {
                        // Format expiry as MM/YY
                        let value = e.target.value.replace(/\D/g, '');
                        if (value.length >= 2) {
                          value = value.slice(0, 2) + '/' + value.slice(2, 4);
                        }
                        setCardDetails({ ...cardDetails, expiry: value });
                      }}
                      slotProps={{ htmlInput: { maxLength: 5 } }}
                    />
                    <TextField
                      fullWidth
                      required
                      label="CVV"
                      placeholder="123"
                      type="password"
                      value={cardDetails.cvv}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        setCardDetails({ ...cardDetails, cvv: value });
                      }}
                      slotProps={{ htmlInput: { maxLength: 4 } }}
                    />
                  </Box>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button variant="outlined" onClick={handleBack}>
                  Back
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleNext}
                  disabled={loading}
                >
                  {loading ? 'Processing...' : <>Pay <RialSymbol /> {total.toFixed(2)}</>}
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Order Total
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 2 }}>
                <RialSymbol /> {total.toFixed(2)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {cart.totalItems} item{cart.totalItems !== 1 ? 's' : ''}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Step 2: Confirmation */}
      {activeStep === 2 && (
        <Card sx={{ maxWidth: 600, mx: 'auto' }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <SuccessIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
              Order Placed Successfully!
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 4 }}>
              Your order has been confirmed and will be ready for pickup soon.
            </Typography>

            <Box
              sx={{
                bgcolor: '#f5f5f5',
                p: 3,
                borderRadius: 2,
                mb: 3,
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Order ID
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {orderId}
              </Typography>
            </Box>

            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              Your order is now pending. You'll receive a confirmation once it's ready for pickup. Show your order QR code to the delivery partner for confirmation.
            </Alert>

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleViewOrder}
              sx={{ mb: 2 }}
            >
              View My Orders
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate('/surprise-boxes')}
            >
              Continue Shopping
            </Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
